import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Car, Plane, Train, Clock, Phone, Mail, ExternalLink  } from 'lucide-react';
import InteractiveMap from '../components/InteractiveMap';
import banner from "/images/Weligama-location.jpg"; 


const Location: React.FC = () => {
  const nearbyAttractions = [
    {
      name: 'Weligama Beach',
      distance: '0.2 miles',
      description: 'Beautiful sandy beach ideal for surfing and relaxing in the sun'
    },
    {
      name: 'Taprobane Island',
      distance: '0.6 miles',
      description: 'Iconic private island with rich history and stunning views'
    },
    {
      name: 'Midigama Surf Point',
      distance: '2.5 miles',
      description: 'Popular surf break for experienced surfers'
    },
    {
      name: 'Snake Farm',
      distance: '4.1 miles',
      description: 'Local conservation center showcasing Sri Lanka\'s reptiles'
    },
    {
      name: 'Mirissa Beach & Whale Watching',
      distance: '5.2 miles',
      description: 'Whale watching tours and vibrant beach restaurants'
    },
    {
      name: 'Galle Fort',
      distance: '15 miles',
      description: 'UNESCO World Heritage Site with colonial architecture and boutique shops'
    },
    {
      name: 'Kushtarajagala Statue',
      distance: '3.2 miles',
      description: 'Ancient Buddhist rock carving and meditation site'
    },
    {
      name: 'Weligama Bay Marriott Resort',
      distance: '1.8 miles',
      description: 'Luxury dining with ocean views'
    }
  ];

  const transportOptions = [
    {
      icon: Car,
      title: 'By Car',
      time: '2.5 hours from Colombo',
      description: 'Free private parking available. GPS coordinates: 5.9730, 80.4298'
    },
    {
      icon: Plane,
      title: 'By Air',
      time: '30 minutes from Koggala Airport',
      description: 'Airport pickup service available upon request.'
    },
    {
      icon: Train,
      title: 'By Train',
      time: '3 hours from Colombo Fort Station',
      description: 'Scenic coastal train route to Weligama Railway Station.'
    }
  ];


  return (
    <div className="min-h-screen bg-primary-50">
      {/* Hero Section */}
      <section className="relative h-96 bg-earth-brown">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${banner})`
          }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex items-center justify-center h-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4">Location & Directions</h1>
            <p className="text-xl md:text-2xl">Discover Weligama, Sri Lanka</p>
          </motion.div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              Explore Villa DINOOS Location
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Located in the beautiful coastal town of Weligama, Sri Lanka. Click on the markers to discover nearby attractions and amenities.
            </p>
          </motion.div>

          <InteractiveMap />
        </div>
      </section>

      {/* Transportation Options */}
      <section className="py-16 bg-earth-beige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              How to Get Here
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Multiple convenient transportation options to reach your peaceful retreat in Weligama
            </p>
          </motion.div>
           {/* Google Maps Card — centered and above transport options */}
            <motion.a
              href="https://www.google.com/maps/place/Villa+Dinoos-Weligama/@5.9801632,80.4199545,17z/data=!4m9!3m8!1s0x3ae1155d55c20147:0x3a8e21256cbec149!5m2!4m1!1i2!8m2!3d5.9801632!4d80.4251043!16s%2Fg%2F11fq5g4rzh?entry=ttu&g_ep=EgoyMDI1MDgwNi4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6 text-center cursor-pointer hover:shadow-xl transition-shadow flex flex-col items-center justify-center mb-12"
            >
              <div className="w-16 h-16 bg-earth-brown rounded-full flex items-center justify-center mx-auto mb-4">
                <ExternalLink className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-earth-brown mb-2">
                Open in Google Maps
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Click to view Villa DINOOS location on Google Maps
              </p>
            </motion.a>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {transportOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6 text-center"
              >
                <div className="w-16 h-16 bg-earth-brown rounded-full flex items-center justify-center mx-auto mb-4">
                  <option.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-earth-brown mb-2">
                  {option.title}
                </h3>
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">{option.time}</span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {option.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Attractions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              Nearby Attractions
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover the natural beauty and local culture surrounding Villa DINOOS in Weligama
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nearbyAttractions.map((attraction, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-earth-beige rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-earth-brown">
                    {attraction.name}
                  </h3>
                  <span className="text-sm font-medium text-earth-gold bg-earth-beige px-2 py-1 rounded">
                    {attraction.distance}
                  </span>
                </div>
                <p className="text-gray-600 text-sm">
                  {attraction.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact & Directions */}
      <section className="py-16 bg-earth-brown text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-4">
              Need Directions or Assistance?
            </h2>
            <p className="text-xl text-earth-beige">
              We're here to help make your journey to Weligama as smooth as possible
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h3 className="text-xl font-serif font-semibold mb-4">Contact Information</h3>
              
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-earth-beige" />
                <div>
                  <p className="font-medium">Phone</p>
                  <p className="text-earth-beige">+94 (777) 336-354</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-earth-beige" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-earth-beige">dinoosluxuries@gmail.com</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-earth-beige" />
                <div>
                  <p className="font-medium">Address</p>
                  <p className="text-earth-beige">152, Bandaranayake Lane<br />Weligama, Matara, Sri Lanka</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <h3 className="text-xl font-serif font-semibold mb-4">Quick Tips</h3>
              
              <div className="space-y-3 text-earth-beige">
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-earth-gold rounded-full mt-2 flex-shrink-0"></span>
                  <span>GPS coordinates: 5.9801632, 80.4225294</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-earth-gold rounded-full mt-2 flex-shrink-0"></span>
                  <span>Look for the white wall with brown gate</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-earth-gold rounded-full mt-2 flex-shrink-0"></span>
                  <span>The driveway is paved and suitable for all vehicles</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-earth-gold rounded-full mt-2 flex-shrink-0"></span>
                  <span>Check-in is available from 3:00 PM onwards</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-earth-gold rounded-full mt-2 flex-shrink-0"></span>
                  <span>Call us if you need assistance finding the property</span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;